import { useCallback, useState, FC } from 'react';
import { Box, Button, Flex, Skeleton, SkeletonCircle } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { chakraComponents } from 'chakra-react-select';
import { fieldValidationMessages } from 'app/constants';
import { useAppSelector } from 'app/store';
import { getCurrentProfile } from 'app/store/Profile/selectors';
import { ChakraProfileSearchOption } from 'app/components/Core';
import {
  ChakraAsyncAutocomplete,
  ChakraAsyncAutocompleteProps,
} from 'app/components/Core/ChakraAsyncAutocomplete/ChakraAsyncAutocomplete';
import { ProfileSearchResponse } from 'types/profile';
import { SearchableProfile } from 'types/notification.types';
import { DDL } from 'types/general.types';
import { ContributionClassName, ContributionIdTypes } from 'types/contribution';
import { getIDFieldInfo, getIsniFieldInfo } from '../RoleFields/utils';
import { ContributionFormData } from '../types';
import { getPersonalSearchableProfile } from '../utils';

interface ContributorSearchProps
  extends Omit<
    Partial<
      ChakraAsyncAutocompleteProps<SearchableProfile, ProfileSearchResponse>
    >,
    'onChange'
  > {
  profilesSearchUrl: string;
  maxSearchLength: number;
  countriesDDL: DDL;
  onChange: (profile: SearchableProfile | null) => void;
}

export const ContributorSearch: FC<ContributorSearchProps> = ({
  profilesSearchUrl,
  maxSearchLength,
  countriesDDL,
  onChange,
  ...rest
}) => {
  const { setValue } = useFormContext<ContributionFormData>();

  const currentProfile = useAppSelector(getCurrentProfile);
  const [inputValue, setInputValue] = useState('');
  const parseResponse = useCallback(
    ({ response }: ProfileSearchResponse) => response.profile.items,
    []
  );
  const maxLengthError =
    inputValue.length > maxSearchLength
      ? fieldValidationMessages.MAX_CHAR(maxSearchLength)
      : '';

  return (
    <ChakraAsyncAutocomplete<SearchableProfile, ProfileSearchResponse>
      initialOption={
        currentProfile && getPersonalSearchableProfile(currentProfile)
      }
      maxSearchLength={maxSearchLength}
      placeholder="Search or add name"
      getOptionLabel={(option) => option?.displayName || ''}
      searchURL={profilesSearchUrl}
      getResponseItems={parseResponse}
      getOptionValue={(option) => option?.guid}
      getNextResponseLink={({ response }) => response?.profile?._links?.next}
      errorMessageProps={{ 'data-testid': 'contributor-search-error-text' }}
      dataTestId="contributor-search"
      components={{
        Option: ({ children, ...props }) => {
          // eslint-disable-next-line no-unused-vars
          const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
          const newProps = { ...props, innerProps: rest };

          return (
            <chakraComponents.Option {...newProps} key={props.data.guid}>
              <ChakraProfileSearchOption
                option={props.data}
                countriesDDL={countriesDDL}
                currentProfileGuid={currentProfile?.guid}
              >
                {children}
              </ChakraProfileSearchOption>
            </chakraComponents.Option>
          );
        },
        NoOptionsMessage: () => null,

        Menu: ({ children, isLoading, ...props }) => {
          return (
            <chakraComponents.Menu isLoading={isLoading} {...props}>
              {children}
              <Box
                height={49}
                bg="surface1"
                color="textBase"
                borderTop={
                  props.options.length > 0 || isLoading ? '1px solid' : 'none'
                }
                borderColor="borderBase"
              >
                <Button
                  isDisabled={Boolean(maxLengthError || !inputValue.trim())}
                  variant="text"
                  paddingLeft={4}
                  w="full"
                  h="full"
                  justifyContent="flex-start"
                  alignItems="center"
                  onClick={() => {
                    setValue('name', inputValue.trim(), {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  data-testid="ContributorSearch-add-manually-btn"
                >
                  Add manually
                </Button>
              </Box>
            </chakraComponents.Menu>
          );
        },
      }}
      loadingSkeleton={
        <Flex height={29} alignItems="center" px={4}>
          <SkeletonCircle width="32px" height="32px" />
          <Flex ml={4} flexDirection="column" justifyContent="space-between">
            <Skeleton height="12px" width={10} mb={1} />
            <Skeleton height="8px" width={6} />
          </Flex>
        </Flex>
      }
      onChange={(value) => {
        if (onChange) {
          onChange(value || null);
        }

        if (typeof value !== 'string' && value !== null && value) {
          setValue('name', value.displayName, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue('contributor', value, { shouldValidate: true });
          setValue('profileGuid', value.guid, {
            shouldValidate: true,
            shouldDirty: true,
          });

          Object.values(ContributionClassName).forEach((fieldKey) => {
            const fieldDetails = getIDFieldInfo(fieldKey);
            const idFieldName = fieldDetails?.idFieldName;
            const idFieldValue =
              (fieldDetails?.type === ContributionIdTypes.IPI
                ? value.preferredIpi?.number.toString()
                : value.preferredIpn?.number.toString()) || '';
            const isniFieldName = getIsniFieldInfo(fieldKey)?.isniFieldName;
            const isniFieldValue = value.preferredIsni?.number.toString() || '';
            if (idFieldName) {
              setValue(idFieldName, idFieldValue, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            if (isniFieldName) {
              setValue(isniFieldName, isniFieldValue, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
          });
        }
      }}
      onInputChange={setInputValue}
      {...rest}
    />
  );
};
